import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Subscription } from 'rxjs';

import { ButtonType, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import { GtmService } from '@fcom/common/gtm';
import { FormStatus, AnalyticsFormStatus } from '@fcom/service-forms';
import { ProfileService } from '@fcom/loyalty-profile/services/profile.service';
import { Profile } from '@fcom/core-api/login';
import { finnairPlusNumberValidator } from '@fcom/service-forms/services/form-validators';
import { EMAIL_PATTERN, NAME_PATTERN, unsubscribe } from '@fcom/core/utils';
import { trackFormState } from '@fcom/service-forms/services/utils';
import { GuardianInformationSubmissionData } from '@fcom/loyalty-profile';

import { GuardianInformationFormField } from '../../interfaces';

export enum GuardianInformationHostingComponent {
  UPDATE_PROFILE,
  LOST_CARD,
}

const ANALYTICS_FORM_NAME = 'update-profile-guardian-information';

@Component({
  selector: 'fin-guardian-information-form',
  templateUrl: 'guardian-information-form.component.html',
  styleUrls: ['./guardian-information-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GuardianInformationFormComponent implements OnInit, OnDestroy {
  readonly FormStatus = FormStatus;
  readonly GuardianInformationFormField = GuardianInformationFormField;
  readonly GuardianInformationHostingComponent = GuardianInformationHostingComponent;
  readonly ButtonType = ButtonType;
  readonly NotificationTheme = NotificationTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input()
  hostingComponent: GuardianInformationHostingComponent = GuardianInformationHostingComponent.UPDATE_PROFILE;

  @Input()
  submitButtonText = 'SERVICE_FORMS.serviceForms.save';

  @Output()
  success: EventEmitter<void> = new EventEmitter();

  @ViewChild('notification', { static: false })
  notification: ElementRef;

  reactiveForm: UntypedFormGroup;
  formStatus: FormStatus = FormStatus.LOADING;

  private subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private profileService: ProfileService,
    private gtmService: GtmService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.profileService.getProfile().subscribe((profile: Profile) => {
        this.createForm(profile);
        this.formStatus = FormStatus.INITIAL;
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  onSubmit(): void {
    if (!this.reactiveForm.valid || this.formStatus === FormStatus.LOADING) {
      this.reactiveForm.markAllAsTouched();

      return;
    }

    this.formStatus = FormStatus.LOADING;

    trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);

    const data: GuardianInformationSubmissionData = {
      [GuardianInformationFormField.GUARDIAN_NAME]: this.reactiveForm.value[GuardianInformationFormField.GUARDIAN_NAME],
      [GuardianInformationFormField.GUARDIAN_PHONE_NUMBER]:
        this.reactiveForm.value[GuardianInformationFormField.GUARDIAN_PHONE_NUMBER],
      [GuardianInformationFormField.GUARDIAN_EMAIL]:
        this.reactiveForm.value[GuardianInformationFormField.GUARDIAN_EMAIL],
      [GuardianInformationFormField.GUARDIAN_MEMBER_NUMBER]:
        this.reactiveForm.value[GuardianInformationFormField.GUARDIAN_MEMBER_NUMBER],
    };

    this.subscriptions.add(
      this.profileService.updateGuardianInformation(data).subscribe({
        next: (result) => {
          if (result.status.message === 'OK') {
            this.formStatus = FormStatus.COMPLETE;
            this.success.emit();
            if (this.hostingComponent !== GuardianInformationHostingComponent.LOST_CARD) {
              this.scrollToNotification();
            }
            trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
          } else {
            this.formStatus = FormStatus.ERROR;
            this.scrollToNotification();
            trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
          }
        },
        error: () => {
          this.formStatus = FormStatus.ERROR;
          this.scrollToNotification();
          trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
        },
      })
    );
  }

  private createForm(profile: Profile): void {
    const formConfig = {
      [GuardianInformationFormField.GUARDIAN_NAME]: [
        profile.guardianName,
        [Validators.required, Validators.pattern(NAME_PATTERN)],
      ],
      [GuardianInformationFormField.GUARDIAN_PHONE_NUMBER]: [
        profile.guardianPhoneNumber,
        [Validators.required, Validators.pattern(/^\+?[0-9\s]{1,15}$/)],
      ],
      [GuardianInformationFormField.GUARDIAN_EMAIL]: [
        profile.guardianEmail,
        [Validators.required, Validators.pattern(EMAIL_PATTERN)],
      ],
      [GuardianInformationFormField.GUARDIAN_MEMBER_NUMBER]: [
        profile.guardianMemberNumber,
        [Validators.required, finnairPlusNumberValidator()],
      ],
    };

    if (this.hostingComponent === GuardianInformationHostingComponent.LOST_CARD) {
      formConfig[GuardianInformationFormField.GUARDIAN_CONSENT] = [false, [Validators.requiredTrue]];
    }

    this.reactiveForm = this.fb.group(formConfig);
  }

  private scrollToNotification(): void {
    this.cdRef.detectChanges();
    this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
